import React, { Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import Layout from './components/layout/Layout'
import { AuthProvider } from './contexts/AuthContext'
import { DarkModeProvider } from './contexts/DarkModeContext'
import { AdminRoute, ClientRoute, EmployeeRoute } from './components/admin/ProtectedRoute'
import ErrorBoundary from './components/ErrorBoundary'
import { LoadingSpinner } from './components/ui/LoadingSpinner'

// Lazy load pages for code splitting
const Home = lazy(() => import('./pages/Home'))
const ClientLoginPage = lazy(() => import('./pages/Login/Client'))
const EmployeeLoginPage = lazy(() => import('./pages/Login/Employee'))
const RegisterPage = lazy(() => import('./pages/Register/Register'))
const CalculatorPage = lazy(() => import('./pages/Calculator/Calculator'))
const ClientDashboard = lazy(() => import('./pages/Client/Dashboard'))
const EmployeeDashboard = lazy(() => import('./pages/Employee/Dashboard'))
const ScheduleEstimate = lazy(() => import('./pages/ScheduleEstimate'))
const ForgotPasswordPage = lazy(() => import('./pages/Login/ForgotPassword'))
const EmployeeShell = lazy(() => import('./pages/Employee/EmployeeShell'))
const EmployeeFollowUps = lazy(() => import('./pages/Employee/FollowUps'))
const EmployeeClients = lazy(() => import('./pages/Employee/Clients'))
const EmployeeClientProfile = lazy(() => import('./pages/Employee/ClientProfile'))
const EmployeeCalendar = lazy(() => import('./pages/Employee/Calendar'))
const EmployeeInvite = lazy(() => import('./pages/Employee/EmployeeInvite'))
const OpenSolarDashboard = lazy(() => import('./pages/Employee/OpenSolarDashboard'))
const Analytics = lazy(() => import('./pages/Employee/Analytics'))
const AdminShell = lazy(() => import('./pages/Admin/AdminShell'))
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'))
const UsersManagement = lazy(() => import('./pages/Admin/UsersManagement'))
const RolesManagement = lazy(() => import('./pages/Admin/RolesManagement'))
const PermissionsManagement = lazy(() => import('./pages/Admin/PermissionsManagement'))
const OpenSolarSettings = lazy(() => import('./pages/Admin/OpenSolarSettings'))

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[calc(100vh-8rem)]">
    <LoadingSpinner size="lg" text="Loading page..." />
  </div>
)

function App() {
  return (
    <ErrorBoundary>
      <DarkModeProvider>
        <AuthProvider>
          <Toaster position="top-right" richColors closeButton />
          <Router>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Layout />}>
                  <Route index element={<Home />} />
                  <Route path="login/client" element={<ClientLoginPage />} />
                  <Route path="login/employee" element={<EmployeeLoginPage />} />
                  <Route path="register" element={<RegisterPage />} />
                  <Route path="calculator" element={<CalculatorPage />} />
                  <Route path="schedule-estimate" element={<ScheduleEstimate />} />
                  <Route path="forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="employee/invite" element={<EmployeeInvite />} />
                  <Route
                    path="client/dashboard"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <ClientRoute>
                          <ClientDashboard />
                        </ClientRoute>
                      </Suspense>
                    }
                  />
                  <Route
                    path="employee"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <EmployeeRoute>
                          <EmployeeShell />
                        </EmployeeRoute>
                      </Suspense>
                    }
                  >
                    <Route index element={<Navigate to="dashboard" replace />} />
                    <Route path="dashboard" element={<EmployeeDashboard />} />
                    <Route path="follow-ups" element={<EmployeeFollowUps />} />
                    <Route path="clients" element={<EmployeeClients />} />
                    <Route path="clients/:clientId" element={<EmployeeClientProfile />} />
                    <Route path="calendar" element={<EmployeeCalendar />} />
                    <Route path="opensolar" element={<OpenSolarDashboard />} />
                    <Route path="analytics" element={<Analytics />} />
                  </Route>
                  <Route
                    path="admin"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <AdminRoute>
                          <AdminShell />
                        </AdminRoute>
                      </Suspense>
                    }
                  >
                    <Route index element={<AdminDashboard />} />
                    <Route path="users" element={<UsersManagement />} />
                    <Route path="roles" element={<RolesManagement />} />
                    <Route path="permissions" element={<PermissionsManagement />} />
                    <Route path="opensolar" element={<OpenSolarSettings />} />
                  </Route>
                </Route>
              </Routes>
            </Suspense>
          </Router>
        </AuthProvider>
      </DarkModeProvider>
    </ErrorBoundary>
  )
}

export default App
